import React from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseRounded from '@mui/icons-material/CloseRounded';
import DownloadRounded from '@mui/icons-material/DownloadRounded';
import { SmartTable } from '../../components/table/SmartTable';
import { fetchScorecardCohortLeads } from '../../services/contactOptimizationService';
import { buildCsv, downloadCsv } from '../../utils/csv';
import type { ScorecardCohortLead, ScorecardCohortRow } from '../../types/contactOptimization';

const PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

/** Matches LEADS_MAX_LIMIT server-side. One request brings the whole bucket,
 * unfiltered, so the chips never need to go back to the warehouse. */
const FETCH_LIMIT = 20000;

/** Mirrors the server's `contacted` filter: anything that is not one of the
 * two terminal not-reached states. Kept here so the chip counts and the grid
 * cannot drift apart from the cohort row's own totals. */
function matchesFilter(lead: ScorecardCohortLead, filter: string): boolean {
  if (filter === 'all') return true;
  if (filter === 'contacted') {
    return lead.outcome !== 'dialed_never' && lead.outcome !== 'never_dialed';
  }
  return lead.outcome === filter;
}

const COLUMNS = [
  'Lead',
  'Contact ID',
  'State',
  'Attempts',
  'First connect',
  'Connected by',
  'Last attempt',
  'Outcome',
];

/**
 * Column types, pinned rather than inferred.
 *
 * SmartTable otherwise guesses from the rows it is handed, and rule 3 of its
 * detection order is "low-cardinality text becomes categorical". With all
 * 277 rows the Lead, Contact ID and State columns have too many distinct
 * values to qualify and render as plain text; filter down to the 20
 * contacted leads and the same columns suddenly qualify and sprout coloured
 * badges. Same column, different look, purely because the filter changed.
 *
 * Declaring the semantics fixes each column's rendering for good. The rule
 * behind the choices: badges for small closed vocabularies where colour aids
 * scanning, plain text for identifiers, free text and timestamps, where
 * colour is just noise.
 *
 * `record_id` also earns its keep beyond consistency: it renders with a
 * copy-to-clipboard control, and copying that id into HubSpot is exactly
 * what this column is for.
 *
 * Layout, which took two goes to get right. SmartTable gives `flex: 1` to
 * `text` and `long_text` columns, and MUI ignores `width` when `flex` is
 * set -- a flexing column gets `minWidth` plus a share of leftover space.
 * The seven other columns here have a 120px floor each, so on a dialog this
 * wide there is no leftover at all, and Lead collapsed to the 80px default
 * floor despite holding much the longest values. Hence COLUMN_MIN_WIDTHS
 * below: Lead needs a real floor, not a share of nothing.
 */
/** Lead carries the longest values by far and is the only column that
 * genuinely needs room. See the note above the component for why a minimum
 * rather than a width. */
const COLUMN_MIN_WIDTHS = { 'Lead': 260 };

const COLUMN_HINTS = {
  columnSemantics: {
    'Lead': 'long_text',
    'Contact ID': 'record_id',
    'State': 'categorical',
    'Attempts': 'number',
    'First connect': 'categorical',
    'Connected by': 'categorical',
    'Last attempt': 'categorical',
    'Outcome': 'categorical',
  },
};

const OUTCOME_LABEL: Record<string, string> = {
  later: 'Contacted (later)',
  dialed_never: 'Dialed, no connect',
  never_dialed: 'Never dialed',
};

function outcomeLabel(outcome: string): string {
  if (OUTCOME_LABEL[outcome]) return OUTCOME_LABEL[outcome];
  return outcome.startsWith('d') ? `Contacted (+${outcome.slice(1)}d)` : outcome;
}

/** SmartTable takes `any[][]`, so leads flatten to positional rows. Numbers
 * stay numbers so the grid sorts and filters them numerically. */
function toRow(lead: ScorecardCohortLead): any[] {
  return [
    lead.label,
    lead.contact_id,
    lead.state ?? '',
    lead.attempts,
    lead.first_connect_offset === null
      ? ''
      : `+${lead.first_connect_offset}d${lead.connect_hour ? ` ${lead.connect_hour}` : ''}`,
    // owner_full_nm is null on some Regal rows, so this is genuinely blank
    // sometimes rather than unknown.
    lead.connected_by ?? '',
    lead.last_attempt_at ?? lead.last_attempt_dt ?? '',
    outcomeLabel(lead.outcome),
  ];
}

/**
 * The leads behind one cohort row.
 *
 * **Fetches the whole bucket in one request and paginates client-side.**
 * That looks wasteful and is not: the leads query rebuilds the entire
 * call_facts CTE whatever the LIMIT, so it costs the same either way --
 * measured at 15.8s for 10 rows against 15.3s for all 277. Server-side
 * paging therefore paid a fresh 15-second query on every page click, and
 * SmartTable rendered "No results found" throughout, because it has no
 * loading state of its own. One fetch means one wait, then instant paging,
 * and sorting and filtering that span the whole bucket rather than the
 * visible page.
 *
 * Everything after that first fetch is local: the outcome chips, paging,
 * sorting, column filters and the CSV export all work on the rows already
 * in memory. Nothing here goes back to the warehouse until a different
 * cohort row is opened.
 *
 * Opens unfiltered on purpose. The point of the row is the whole cohort;
 * the chips narrow it afterwards.
 *
 * Uses SmartTable so this inherits the sorting, column filtering, density
 * and toolbar every other grid in the product has.
 */
export default function CohortLeadsDialog({
  row,
  open,
  onClose,
}: {
  row: ScorecardCohortRow | null;
  open: boolean;
  onClose: () => void;
}) {
  const [filter, setFilter] = React.useState<string>('all');
  const [leads, setLeads] = React.useState<ScorecardCohortLead[]>([]);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const bucketStart = row?.bucket_start;
  const bucketEnd = row?.bucket_end;

  // A different row is a different result set, so drop the filter with it.
  React.useEffect(() => {
    setFilter('all');
  }, [bucketStart, bucketEnd]);

  // Deliberately NOT keyed on `filter`. The whole bucket is fetched once,
  // unfiltered; the chips then narrow what is already in memory. Filtering
  // server-side would mean a fresh ~15s query per chip click for rows we
  // already hold.
  React.useEffect(() => {
    if (!open || !bucketStart || !bucketEnd) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchScorecardCohortLeads({
      bucketStart,
      bucketEnd,
      limit: FETCH_LIMIT,
      offset: 0,
    })
      .then((res) => {
        if (cancelled) return;
        setLeads(res.leads);
        setTotal(res.total);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load leads');
          setLeads([]);
          setTotal(0);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, bucketStart, bucketEnd]);

  const rows = React.useMemo(
    () => leads.filter((l) => matchesFilter(l, filter)).map(toRow),
    [leads, filter],
  );

  /** Straight from what is already loaded, so it is instant and cannot
   * disagree with what is on screen. */
  const exportCsv = () => {
    if (!row) return;
    downloadCsv(
      `cohort-leads-${row.bucket_start}${filter === 'all' ? '' : `-${filter}`}.csv`,
      buildCsv(COLUMNS, rows),
    );
  };

  const chips = row
    ? [
        { id: 'all', label: 'All', count: row.leads },
        { id: 'contacted', label: 'Contacted', count: row.total_contacted },
        { id: 'dialed_never', label: 'Dialed, no connect', count: row.dialed_never },
        { id: 'never_dialed', label: 'Never dialed', count: row.never_dialed },
      ]
    : [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pb: 1.25 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, letterSpacing: '-0.01em' }}>
              Leads that arrived {row?.label ?? ''}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {row ? `${row.leads.toLocaleString()} leads` : ''}
              {row && row.bucket_start !== row.bucket_end ? ` · ${row.bucket_start} to ${row.bucket_end}` : ''}
              {row?.partial ? ' · clipped by the selected range' : ''}
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title={`Download all ${rows.length.toLocaleString()} rows shown as CSV`} arrow>
              <span>
                <IconButton
                  size="small"
                  onClick={exportCsv}
                  disabled={loading || !rows.length}
                  aria-label="Download these leads as CSV"
                >
                  <DownloadRounded sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
            <IconButton size="small" onClick={onClose} aria-label="Close">
              <CloseRounded sx={{ fontSize: 18 }} />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 2 }}>
        <Stack direction="row" spacing={1} sx={{ mb: 1.75, flexWrap: 'wrap', gap: 1 }}>
          {chips.map((chip) => (
            <Chip
              key={chip.id}
              size="small"
              label={`${chip.label} ${chip.count.toLocaleString()}`}
              color={filter === chip.id ? 'primary' : 'default'}
              variant={filter === chip.id ? 'filled' : 'outlined'}
              onClick={() => setFilter(chip.id)}
              disabled={loading}
            />
          ))}
        </Stack>

        {error ? (
          <Typography variant="body2" color="error" sx={{ py: 2 }}>
            {error}
          </Typography>
        ) : loading ? (
          // Explicit, because the query takes around fifteen seconds and an
          // empty grid saying "No results found" reads as a failure.
          <Stack alignItems="center" spacing={1.5} sx={{ py: 7 }}>
            <CircularProgress size={24} />
            <Typography variant="caption" color="text.secondary">
              Loading leads from the warehouse, this takes a few seconds
            </Typography>
          </Stack>
        ) : (
          <SmartTable
            // Remounts per filter so the grid resets to page 1 on a new set.
            key={`${bucketStart}-${bucketEnd}-${filter}`}
            columns={COLUMNS}
            rows={rows}
            hints={COLUMN_HINTS}
            columnMinWidths={COLUMN_MIN_WIDTHS}
            rowCount={rows.length}
            // Only true past FETCH_LIMIT, which a bucket realistically never
            // reaches. Everything else is fully loaded, so paging, sorting
            // and filtering all run locally and instantly.
            truncated={total > leads.length}
            variant="embed"
            initialPageSize={PAGE_SIZE}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            showToolbar
            // Every field is already a column here, so the per-row drawer
            // would only repeat the row you just clicked. It defaults on.
            enableRowDetail={false}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
